import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { User } from 'shared'
import { Alert, Button, Field, FullPageSpinner, Input } from '../components/ui'
import { homePathFor, useAuth } from '../contexts/AuthContext'
import { api } from '../utils/api'
import { toErrorMessage } from '../lib/errors'
import Logo from '../components/ui/Logo'

/** Mirrors the backend's shared rule; the server checks it again regardless. */
const MIN_PASSWORD_LENGTH = 8

/**
 * Dove si sceglie la nuova password, dal link ricevuto per email.
 *
 * Un link sbagliato, scaduto o già usato dicono tutti la stessa cosa. La pagina
 * non mostra a chi appartiene il link: chi lo apre lo sa già, e chi lo ha
 * trovato non deve impararlo da qui.
 */
export default function ResetPassword() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { token } = useParams<{ token: string }>()
  const { setSession } = useAuth()

  const [valid, setValid] = useState(false)
  const [loading, setLoading] = useState(true)
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .get(`/api/password-reset/${token}`)
      .then(() => setValid(true))
      .catch(() => setValid(false))
      .finally(() => setLoading(false))
  }, [token])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    setError(null)

    try {
      const response = await api.post<{ token: string; user: User }>(
        `/api/password-reset/${token}`,
        { password }
      )

      // The session starts here: someone who has just chosen a password should
      // not have to type it again. Every other session of theirs is already closed.
      setSession(response.token, response.user)
      navigate(homePathFor(response.user), { replace: true })
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
      setSubmitting(false)
    }
  }

  if (loading) return <FullPageSpinner />

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 dark:bg-gray-950">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <Logo />
        </div>

        <div className="rounded-lg bg-white p-8 shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
          {!valid ? (
            <div className="text-center">
              <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
                {t('passwordReset.invalidTitle')}
              </h1>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {t('passwordReset.invalidDescription')}
              </p>
              <Link
                to="/forgot-password"
                className="mt-6 inline-block text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
              >
                {t('passwordReset.askAgain')}
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
                {t('passwordReset.resetTitle')}
              </h1>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {t('passwordReset.resetDescription')}
              </p>

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <Field
                  label={t('auth.password')}
                  required
                  hint={t('auth.passwordHint', { min: MIN_PASSWORD_LENGTH })}
                >
                  <Input
                    type="password"
                    name="new-password"
                    required
                    minLength={MIN_PASSWORD_LENGTH}
                    autoComplete="new-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </Field>

                {error && <Alert>{error}</Alert>}

                <Button type="submit" className="w-full" loading={submitting}>
                  {t('passwordReset.submit')}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
